/**
 * Frozen Voice ops / orchestrator stats contract.
 * Rates are 0–1 fractions (null when undefined). Never includes OTP digits.
 */

export type AsrMode = "human" | "network";

export type MixCounts = Record<string, number>;

export interface FailureAnatomy {
  trunk: number;
  dest: number;
  quality: number;
  policy: number;
}

export interface VoiceMix {
  human: number;
  screening: number;
  machine: number;
  other: number;
}

export interface HangupSlices {
  sip: number;
  no_media: number;
  dest: number;
  policy: number;
  other: number;
}

export interface OtpFunnel {
  attempts: number;
  human: number;
  prompted: number;
  no_digits: number;
  collected: number;
  verified: number;
  mismatch: number;
  not_reached: number;
}

export interface DayPartHour {
  hour: number;
  label: string;
  attempts: number;
  human: number;
  screening: number;
  machine: number;
  other: number;
  human_asr: number | null;
}

export interface DayPartPayload {
  tz: string;
  clock: string;
  n: number;
  unclocked?: number;
  sample_thin?: boolean;
  n_min?: number;
  hours: DayPartHour[];
  voice_mix?: VoiceMix;
  source?: string;
  live_routing: boolean;
}

export interface Measures {
  attempts: number;
  human_asr: number | null;
  network_asr: number | null;
  quality_fail_rate: number | null;
  rotate_rate: number | null;
  human_n: number;
  network_n: number;
  quality_n: number;
  sample_thin: boolean;
  n_min: number;
}

export interface OverviewWindow {
  last_15m: Measures;
  yesterday_same_hour: Measures | null;
}

export interface Overview extends Measures {
  agents: number;
  shadow: number;
  live: number;
  amd_mix: MixCounts;
  classification_mix: MixCounts;
  failure_anatomy: FailureAnatomy;
  p95_pdd: number | null;
  p50_mos: number | null;
  policy_guard: boolean;
  asr_modes: AsrMode[];
  window: OverviewWindow;
  voice_mix?: VoiceMix;
  hangup_slices?: HangupSlices;
  outcome_mix?: MixCounts;
  otp_funnel?: OtpFunnel;
  top_cells?: TopCell[];
  day_part?: DayPartPayload;
  source?: string;
  live_routing?: boolean;
}

export interface TopCell {
  trunk_id: string;
  country_iso2: string;
  attempts: number;
  human_asr: number | null;
  voice_mix?: VoiceMix;
  hangup_slices?: HangupSlices;
  sample_thin?: boolean;
}

export interface MatrixCell extends Pick<
  Measures,
  "attempts" | "human_asr" | "network_asr" | "quality_fail_rate" | "sample_thin"
> {
  trunk_id: string;
  country_iso2: string;
  prefix_group: string | null;
  failure_anatomy: FailureAnatomy;
  amd_mix: MixCounts;
  classification_mix: MixCounts;
  voice_mix?: VoiceMix;
  hangup_slices?: HangupSlices;
  human_n?: number;
  network_n?: number;
  quality_n?: number;
  n_min?: number;
  rotate_rate?: number | null;
}

export interface MatrixPayload {
  n: number;
  sample_thin: boolean;
  n_min: number;
  cells: MatrixCell[];
  live_routing: boolean;
  voice_mix?: VoiceMix;
  hangup_slices?: HangupSlices;
  source?: string;
}

export interface BestPathRow {
  country_iso2?: string | null;
  trunk_id: string;
  prefix_group?: string | null;
  window?: string | null;
  score: number | null;
  confidence: number | null;
  n: number;
  eligible: boolean;
  reasons: string[];
  shadow?: boolean;
  live_routing?: boolean;
  updated_at?: string | null;
}

export interface BestPathBest {
  country_iso2: string;
  trunk_id: string;
  score: number | null;
  confidence: number | null;
  n: number;
  eligible: boolean;
  reasons: string[];
  shadow: boolean;
  live_routing: boolean;
}

export interface BestPathPayload {
  shadow: boolean;
  live_routing: boolean;
  label: string;
  best: BestPathBest[];
  ranked: BestPathRow[];
  n_min?: number;
  sample_thin?: boolean;
}

export interface ClassificationPayload {
  classification_mix: MixCounts;
  hangup_cause: MixCounts;
  n?: number;
  sample_thin?: boolean;
  n_min?: number;
}

export interface FailuresPayload {
  failure_anatomy: FailureAnatomy;
  hangup_slices?: HangupSlices;
  hangup_cause?: MixCounts;
  success_class: MixCounts;
  reason_codes: MixCounts;
  rotate_rate: number | null;
  live_routing: boolean;
  n?: number;
  sample_thin?: boolean;
  n_min?: number;
  source?: string;
}

export interface AmdQualityPayload {
  amd_mix: MixCounts;
  connecting_leftover: number;
  empty_rx: number;
  human: number;
  voicemail: number;
  notes: string[];
  n?: number;
  sample_thin?: boolean;
  n_min?: number;
}

export interface TrunkHealth {
  id: string;
  state: string | null;
  status: string | null;
  display?: string | null;
}

export interface CatalogRow {
  iso2: string;
  iso3?: string | null;
  name: string;
  calling_code: string;
  nsn_min: number;
  nsn_max: number;
  notes?: string | null;
}

export interface CatalogPayload {
  catalog: CatalogRow[];
}

export interface JudgmentRow {
  call_id: string;
  outcome: string;
  outcome_reason: string;
  far_end_class: string;
  success_class: string;
  trunk_id: string;
}

export interface JudgmentsPayload {
  judgments: JudgmentRow[];
  outcome_mix?: MixCounts;
  n?: number;
  source?: string;
  live_routing?: boolean;
}

export interface OtpFunnelPayload {
  otp: MixCounts;
  funnel?: OtpFunnel;
  no_digits?: number;
  otp_events?: MixCounts;
  n?: number;
  source?: string;
  live_routing?: boolean;
  notes?: string[];
}

export interface AgentRow {
  agent_id: string;
  display_name: string;
  class: string;
  status: string;
  owns: string;
  prompt_ref: string;
}

export interface AgentsPayload {
  agents: AgentRow[];
}

export interface PageRow {
  page_id: string;
  title: string;
  job: string;
  status: string;
  owner_agent_id: string;
}

export interface PagesPayload {
  pages: PageRow[];
}

export const EMPTY_ANATOMY: FailureAnatomy = {
  trunk: 0,
  dest: 0,
  quality: 0,
  policy: 0,
};

export const ANATOMY_KEYS: Array<keyof FailureAnatomy> = ["trunk", "dest", "quality", "policy"];

export const VOICE_KEYS: Array<keyof VoiceMix> = ["human", "screening", "machine", "other"];

export const SLICE_KEYS: Array<keyof HangupSlices> = ["sip", "no_media", "dest", "policy", "other"];

export const EMPTY_VOICE: VoiceMix = { human: 0, screening: 0, machine: 0, other: 0 };

export const EMPTY_SLICES: HangupSlices = { sip: 0, no_media: 0, dest: 0, policy: 0, other: 0 };
